import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { Plus, Search, Layers, Trash2, ChevronRight } from './components/DeskeyIcons';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Skeleton } from './components/ui/skeleton';
import { confirmDialog } from './components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClasses() {
  const { user, token } = useApp();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createClassOpen, setCreateClassOpen] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', grade_level: '' });

  const fetchAll = async () => {
    const tid = user?.tenant_id;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [cl, se, su] = await Promise.all([
        axios.get(`${API}/classes`, { params: { tenant_id: tid }, headers }),
        axios.get(`${API}/sections`, { params: { tenant_id: tid }, headers }),
        axios.get(`${API}/subjects`, { params: { tenant_id: tid }, headers }),
      ]);
      setClasses(cl.data);
      setSections(se.data);
      setSubjects(su.data);
    } catch { toast.error('Failed to load academic structure'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [user?.tenant_id]);

  const createClass = async () => {
    if (!newClass.name) { toast.error('Class name required'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API}/classes`, { ...newClass, tenant_id: user.tenant_id }, { headers });
      toast.success('Class created');
      setCreateClassOpen(false);
      setNewClass({ name: '', grade_level: '' });
      fetchAll();
    } catch { toast.error('Failed to create class'); }
  };

  const deleteClass = async (id) => {

    if (!(await confirmDialog({ title: 'Delete class', message: "Delete this class? This can't be undone.", danger: true, confirmLabel: 'Delete' }))) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/classes/${id}`, { headers });
      toast.success('Class deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <DashboardShell title="Academic Structure" breadcrumb="Admin Dashboard">
      <div className="space-y-4 fade-in">
        <Tabs defaultValue="classes">
          <TabsList>
            <TabsTrigger value="classes">Classes ({classes.length})</TabsTrigger>
            <TabsTrigger value="sections">Sections ({sections.length})</TabsTrigger>
            <TabsTrigger value="subjects">Subjects ({subjects.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="classes" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>{classes.length} classes configured</p>
              <Dialog open={createClassOpen} onOpenChange={setCreateClassOpen}>
                <DialogTrigger asChild>
                  <Button style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }} size="sm" data-testid="add-class-button">
                    <Plus className="w-4 h-4 mr-2" /> Add Class
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-sm rounded-2xl">
                  <DialogHeader><DialogTitle>Add Class</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm mb-1.5 block">Class Name</Label>
                      <Input placeholder="e.g. Class 10" value={newClass.name} onChange={e => setNewClass(c => ({ ...c, name: e.target.value , headers }))} />
                    </div>
                    <div>
                      <Label className="text-sm mb-1.5 block">Grade Level</Label>
                      <Input placeholder="e.g. 10" value={newClass.grade_level} onChange={e => setNewClass(c => ({ ...c, grade_level: e.target.value }))} />
                    </div>
                    <Button onClick={createClass} className="w-full" style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>Create Class</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? <Skeleton className="h-40 rounded-2xl" /> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {classes.map(cls => {
                  const classSections = sections.filter(s => s.class_id === cls.id);
                  const classSubjects = subjects.filter(s => s.class_id === cls.id);
                  return (
                    <div key={cls.id} className="desky-card p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold">{cls.name}</p>
                          {cls.grade_level && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>Grade {cls.grade_level}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => deleteClass(cls.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-3 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        <span>{classSections.length} sections</span>
                        <span>•</span>
                        <span>{classSubjects.length} subjects</span>
                      </div>
                    </div>
                  );
                })}
                {classes.length === 0 && (
                  <div className="col-span-3 empty-state">
                    <Layers className="empty-state-icon" />
                    <p className="text-sm">No classes yet. Add your first class.</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sections" className="mt-4">
            {sections.length === 0 ? (
              <div className="empty-state">
                <Layers className="empty-state-icon" />
                <p className="text-sm">No sections configured</p>
              </div>
            ) : (
              <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
                <CardContent className="p-0">
                  <table className="w-full desky-table">
                    <thead>
                      <tr>
                        <th className="text-left">Section</th>
                        <th className="text-left">Class</th>
                        <th className="text-left hidden sm:table-cell">Students</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sections.map(s => (
                        <tr key={s.id}>
                          <td className="py-3 px-4"><span className="text-sm font-medium">{s.name}</span></td>
                          <td className="py-3 px-4"><span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>{classes.find(c => c.id === s.class_id)?.name || '—'}</span></td>
                          <td className="py-3 px-4 hidden sm:table-cell"><span className="text-sm">{s.student_count}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="subjects" className="mt-4">
            {subjects.length === 0 ? (
              <div className="empty-state">
                <Layers className="empty-state-icon" />
                <p className="text-sm">No subjects configured</p>
              </div>
            ) : (
              <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
                <CardContent className="p-0">
                  <table className="w-full desky-table">
                    <thead>
                      <tr>
                        <th className="text-left">Subject</th>
                        <th className="text-left hidden sm:table-cell">Code</th>
                        <th className="text-left">Class</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map(s => (
                        <tr key={s.id}>
                          <td className="py-3 px-4"><span className="text-sm font-medium">{s.name}</span></td>
                          <td className="py-3 px-4 hidden sm:table-cell"><span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'hsl(var(--accent))' }}>{s.code}</span></td>
                          <td className="py-3 px-4"><span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>{classes.find(c => c.id === s.class_id)?.name || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  );
}
